import { type HttpError } from '@atlassian/koa-error';

interface V8ErrorConstructor extends ErrorConstructor {
  // eslint-disable-next-line @typescript-eslint/ban-types
  captureStackTrace: (error: object, constructor: Function) => void;
}

export interface BaseErrorOptions {
  name: string;
  ignoreUfoReporting?: boolean;
  meta?: unknown;
}

// eslint-disable-next-line no-restricted-syntax
export class BaseError extends Error {
  ignoreUfoReporting: boolean;
  meta?: unknown;
  constructor(
    { name, ignoreUfoReporting, meta }: BaseErrorOptions,
    ...params: ConstructorParameters<typeof Error>
  ) {
    super(...params);

    // Ensure this constructor is not included in the stack trace (if supported)
    const V8Error = Error as V8ErrorConstructor;
    if (V8Error.captureStackTrace) {
      V8Error.captureStackTrace(this, new.target);
    }

    this.name = name;
    this.ignoreUfoReporting = ignoreUfoReporting ?? false;
    this.meta = meta;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      stack: this.stack,
      meta: this.meta,
    };
  }
}

export const createErrorClass = (name: string) =>
  class extends BaseError {
    constructor(
      message?: string | undefined,
      options?: Partial<BaseErrorOptions> | undefined,
      cause?: Error
    ) {
      super({ name, ...options }, message, { cause });
    }
  };

export const toError = (maybeError: unknown) => {
  return maybeError instanceof Error ? maybeError : new Error(String(maybeError));
};

export function isHttpError(maybeError: unknown): maybeError is Partial<HttpError> {
  if (
    maybeError instanceof Error &&
    maybeError.name === 'HttpError' &&
    'status' in maybeError
  ) {
    return true;
  }
  return false;
}

export const errorOrUndefined = (maybeError: unknown) => {
  return maybeError instanceof Error ? maybeError : undefined;
};
